// @ flow
import React, {useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {useFormik} from "formik";
import {Row, Col, Form, Input, Select, Button, List, Skeleton, Switch} from "antd";
import MaskedInput from "antd-mask-input";
import {NotificationOutlined} from "@ant-design/icons";

import {useTranslation} from "react-i18next";

import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {newUserValidation} from "./newUserValidation";
import {ErrorFocus} from "../../_defaultComponents/ErrorFocus/ErrorFocus";
import {CustomButton} from "../../_defaultComponents/CustomButton/CustomButton";
import {AuthorizedCityUsersModal} from "../../_defaultComponents/AuthorizedUsersModal/AuthorizedUsersModal";
import {QueueIdModal} from "../modals/QueueIdModal";
import {agencyManagedRoles, userTypes} from "../../../constants/constants";
import styled from "styled-components";
import {
    CommunicationPreferencesCardContainer,
    HeaderText
} from "../../_defaultComponents/CommunicationPreferencesCard/CpStyles";
import {CityFilter} from "../../_defaultComponents/FilterComponents";
import {ZoneManagementSelect} from "../../_defaultComponents/ZoneManagementSelect";
import {ZoneSelect} from "../../_defaultComponents/ZoneSelect";
import {PagePermissions} from "./pagePermissions/PagePermissions";

// Kapsamı şehir ve firma/alt firma ile belirlenen roller; subAgency için hepsi opsiyoneldir
// (seçilmezse bağlı olduğu acentenin tüm kapsamı geçerli olur).
const cityScopedRoles = ["oUser", "sChef", "subAgency"];
// Şehir alanı subAgency için gösterilmez: gerçek kapsam bölgeden (zones) türetilir, bu nedenle subAgency oluştururken şehir inputu kaldırıldı.
const citySelectableRoles = cityScopedRoles.filter(role => role !== "subAgency");

export const NewUser = ({userId}) => {
    const stores = useStore();
    const isAgency = stores.meStore.checkRole("isAgency");
    const agencyRoleOptions = stores.meStore.isSubAgency ? ["cUser"] : agencyManagedRoles;
    const roleOptions = isAgency
        ? userTypes.filter(role => agencyRoleOptions.includes(role.value))
        : userTypes;
    const {t} = useTranslation(["newUser", "userTypes", "communicationPreferences"]);
    const history = useHistory();
    const [companies, setCompanies] = useState([]);
    const [cities, setCities] = useState([]);
    const [recall, setRecall] = useState(true);
    const [uatSwitch, setUatSwitch] = useState(false);
    const [isTest, setIsTest] = useState(false);
    const [canCallCouriers, setCanCallCouriers] = useState(false);
    const [queueId, setQueueId] = useState(null);
    const [queueIdModalVisible, setQueueIdModalVisible] = useState(false);
    const recallRef = useRef(recall);
    recallRef.current = recall;

    const [dailyReminder, setDailyReminder] = useState(false);
    const [authorizedUsersModalVisible, setAuthorizedUsersModalVisible] = useState(false);
    const [authorizedUsersData, setAuthorizedUsersData] = useState({
        title: "",
        users: []
    });

    useEffect(() => {
        setTimeout(() => {
            setRecall(!recallRef.current);
        }, 60000);
    }, [recall]);

    //seçilen alt firmalar (her row'da gösterebilmek için company bazında ayrıştırıldı)
    const [subCompanies, setSubCompanies] = useState([[]]);

    //ekrana giriş yapıldığından beri işlem yapılan company'lerin bütün alt firmaları
    const [allSubCompanies, setAllSubCompanies] = useState([[{name: "", _id: ""}]]);
    const [didSubCompaniesLoaded, setDidSubCompaniesLoaded] = useState(false);
    const [didCitiesLoaded, setDidCitiesLoaded] = useState(false);
    const [didCompaniesLoaded, setDidCompaniesLoaded] = useState(false);

    const [, user = {}, , setLoading] = useFetch({
        fetchFn: stores.userStore.getUser.bind(null, {
            userId
        }),
        showLoading: true,
        condition: !!userId,
        callbackFunc: response => {
            if (response?.data?.uat) {
                setUatSwitch(response?.data?.uat);
            }
            setIsTest(response?.data?.isTest ?? false);
            setCanCallCouriers(response?.data?.canCallCouriers === true);
            setQueueId(response?.data?.alotech?.queueId || null);
        }
    });

    const [, companyList, ,] = useFetch({
        fetchFn: stores.companyStore.getCompanyList.bind(null, {
            limit: 3000,
            skip: 0,
            activity: "enabled",
            onboardingStatus: "all"
        }),
        showLoading: true
    });
    const getSubCompany = async (company, index, cities = []) => {
        const result = await stores.companyStore.getSubCompanyList({
            companyId: company,
            limit: 1000,
            activity: "enabled"
        });

        const simpleRows = result?.rows
            ?.filter(row => (cities.length > 0 ? cities.includes(row.city) : true))
            .map(subcomp => ({
                name: subcomp.name,
                _id: subcomp._id,
                city: subcomp.city
            }));

        setAllSubCompanies(prev =>
            prev.map((item, prevIndex) => {
                return prevIndex === index ? simpleRows : item;
            })
        );
    };

    useEffect(() => {
        if (userId) {
            const getCommunicationPreferences = async () => {
                const resultOfRequest = await stores.userStore.getCommunicationPreferences({
                    userId
                });
                setDailyReminder(
                    resultOfRequest?.data.communicationPreferences.email.shift.dailyReminder
                );
            };
            getCommunicationPreferences();
        }
    }, [userId]);

    const formik = useFormik({
        initialValues: {
            username: "",
            email: "",
            role: "",
            fullName: "",
            password: "",
            phone: "",
            isTest: false,
            canCallCouriers: false,
            alotech: {
                queueId: null
            },
            title: "",
            communicationPreferences: false,
            newUser: !user?.data?._id,
            auth: {
                companies: [],
                subcompanies: [],
                cities: [],
                zoneManagement: undefined,
                zones: [],
                auditZones: [],
                pagePermissions: []
            },
            ...(user && user.data),
            auth: {
                companies: [],
                subcompanies: [],
                cities: [],
                zoneManagement: undefined,
                zones: [],
                auditZones: [],
                pagePermissions: [],
                ...(user?.data?.auth || {})
            }
        },
        validationSchema: newUserValidation,
        enableReinitialize: true,
        onSubmit: async values => {
            await setLoading(true);
            values.phone = values.phone.replace(/[-,(,)]/g, "");
            values.uat = uatSwitch;
            values.isTest = isTest;
            values.canCallCouriers = ["admin", "hrEmployment", "ik", "agencyIk"].includes(
                values.role
            )
                ? canCallCouriers
                : false;
            values.alotech = {
                queueId: ["admin", "hrEmployment", "ik", "agencyIk"].includes(values.role)
                    ? queueId
                    : null
            };
            let companyError = "";

            if (["cUser", "sOperation"].includes(values.role)) {
                // Acentenin cUser'ı alt firma ile acenteye bağlanır; alt firma seçimi zorunludur.
                const isAgencySubcompanyMissing =
                    isAgency && values.role === "cUser" && subCompanies.flat().length === 0;
                if (!companies[0] || isAgencySubcompanyMissing) {
                    companyError = t("newUser.validation.companyAndSubcompanyError", {
                        ns: "newUser"
                    });
                } else {
                    const nonEmptyCompanies = companies.filter(n => n);
                    values.auth.companies = nonEmptyCompanies;
                    let unparsedSubCompanies = [];
                    subCompanies.map(subComp => subComp.map(sub => unparsedSubCompanies.push(sub)));
                    values.auth.subcompanies = unparsedSubCompanies;
                }
            } else if (cityScopedRoles.includes(values.role)) {
                const nonEmptyCompanies = companies.filter(n => n);
                values.auth.companies = nonEmptyCompanies;
                let unparsedSubCompanies = [];
                subCompanies.map(subComp => subComp.map(sub => unparsedSubCompanies.push(sub)));
                values.auth.subcompanies = unparsedSubCompanies;
                values.auth.cities = cities;
            } else if (["hrEmployment", "seller"].includes(values.role)) {
                values.auth.cities = cities;
            }

            if (companyError) {
                stores.appStore.openErrorModal({content: companyError});
            } else {
                if (userId) {
                    // Kullanıcı Güncelleme
                    delete values.password;
                    const preferences = {
                        communicationPreferences: {
                            email: {
                                shift: {
                                    dailyReminder: dailyReminder
                                }
                            }
                        }
                    };
                    // Acente updateCommunicationPreferences işlemine sahip değildir; tercih
                    // güncelleme gövdesiyle birlikte gönderilir.
                    const result = await stores.userStore.updateUser({
                        user: isAgency ? {...values, ...preferences} : values
                    });
                    await setLoading(false);
                    if (result && !result?.result?.validationFailed) {
                        if (!isAgency) {
                            await stores.userStore.updateCommunicationPreferences({
                                userId,
                                preferences
                            });
                        }
                        history.push("/users/" + userId);
                    } else if (result?.result?.validationFailed) {
                        setAuthorizedUsersData({
                            title: result?.message,
                            users: result?.result?.authorizedUsersInCities
                        });
                        setAuthorizedUsersModalVisible(true);
                    }
                } else {
                    // Kullanıcı Oluşturma
                    const preferences = {
                        communicationPreferences: {
                            email: {
                                shift: {
                                    dailyReminder: values.communicationPreferences
                                }
                            }
                        }
                    };
                    // Acente updateCommunicationPreferences işlemine sahip değildir; tercih
                    // oluşturma gövdesiyle birlikte gönderilir.
                    const result = await stores.userStore.createUser(
                        isAgency ? {...values, ...preferences} : values
                    );
                    if (result && !result?.result?.validationFailed) {
                        if (!isAgency) {
                            await stores.userStore.updateCommunicationPreferences({
                                userId: result._id,
                                preferences
                            });
                        }
                        history.push("/users/" + result?._id);
                    } else if (result?.result?.validationFailed) {
                        setAuthorizedUsersData({
                            title: result?.message,
                            users: result?.result?.authorizedUsersInCities
                        });
                        setAuthorizedUsersModalVisible(true);
                    }

                    await setLoading(false);
                }
            }
            setLoading(false);
        }
    });
    const {
        values,
        errors,
        touched,
        handleChange,
        handleSubmit,
        isSubmitting,
        setFieldValue
    } = formik;

    useEffect(() => {
        //eski companies verisi varsa ve yeni eklenmemişse
        if (!didCompaniesLoaded && values.auth.companies[0]) {
            //eskiyi yeniye ata
            setCompanies(values.auth.companies);
            setDidCompaniesLoaded(true);

            values.auth.companies.map((comp, index) => {
                //company'lerin bütün sub'larını çek, alt firma seçimini getireceğimiz için lazım
                //bunu yaparken her seferinde default değeri önceden ata
                setAllSubCompanies(prev => [...prev, [{name: "", _id: ""}]]);
                getSubCompany(comp, index, values.auth.cities);
            });
            //eski subcompanies verisi varsa
        } else if (cityScopedRoles.includes(values.role) && companies.length == 0) {
            setCompanies([""]);
        }
        if (!didCitiesLoaded && values.auth.cities[0]) {
            setCities(values.auth.cities);
            setDidCitiesLoaded(true);
        }
        if (
            ["cUser", "sOperation"].includes(values.role) &&
            values.auth.companies.length === 0 &&
            companies.length === 0
        ) {
            setCompanies([{}]);
        }
    }, [values]);

    // Role değiştiğinde canCallCouriers ve queueId'yi sıfırla
    useEffect(() => {
        if (!["admin", "hrEmployment", "ik"].includes(values.role)) {
            setCanCallCouriers(false);
            setQueueId(null);
        }
    }, [values.role]);

    useEffect(() => {
        //eski subcompanies'i varsa ve bunlar state'e henüz atanmamışsa gir, company çekilirken eşleştir.
        //her row'da gösterebilmek için subCompanies state'ini; values.auth.subcompanies'i company bazında ayrıştırarak doldur.

        if (!didSubCompaniesLoaded && values.auth.subcompanies[0]) {
            //ilk yükleme olup olmadığının tespiti checkerForLoading;
            //daha sonraki değişikliklerde useEffect'e girmesi veriyi bozar, bu yüzden yalnızca initial loading'de girmesini sağla.
            let checkerForLoading = 0;

            const parsedSubCompanies = allSubCompanies.map((sub, index) =>
                sub
                    .map(s => s?._id)
                    .filter(s => values.auth.subcompanies.includes(s))
                    .concat(values.auth.subcompanies.filter(x => sub.includes(x)))
            );
            parsedSubCompanies.map(subComp => subComp.map(sub => checkerForLoading++));

            values.auth.subcompanies[0] &&
                checkerForLoading === values.auth.subcompanies.length &&
                setDidSubCompaniesLoaded(true);
            setSubCompanies(parsedSubCompanies);
        }
    }, [allSubCompanies]);

    const onChangeSwitch = async checked => {
        values.communicationPreferences = checked;
        setDailyReminder(checked);
    };

    return (
        <Row type="flex" justify="center" data-testid="newUserScreen">
            <Col span={20}>
                <Form
                    autoComplete="off"
                    data-testid="newUserForm"
                    labelCol={{span: 24}}
                    wrapperCol={{span: 24}}
                    onFinish={handleSubmit}
                >
                    <ErrorFocus formik={formik} />
                    <Form.Item
                        label={t("fields.username", {ns: "common"})}
                        validateStatus={errors.username && touched.username ? "error" : "success"}
                        help={errors.username && touched.username ? errors.username : null}
                    >
                        <Input
                            name="username"
                            data-testid="userNameInput"
                            value={values.username}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    <Form.Item
                        label={t("fields.fullname", {ns: "common"})}
                        data-testid="nameInput"
                        validateStatus={errors.fullName && touched.fullName ? "error" : "success"}
                        help={errors.fullName && touched.fullName ? errors.fullName : null}
                    >
                        <Input name="fullName" value={values.fullName} onChange={handleChange} />
                    </Form.Item>
                    <Form.Item
                        label="E-mail"
                        validateStatus={errors.email && touched.email ? "error" : "success"}
                        help={errors.email && touched.email ? errors.email : null}
                    >
                        <Input
                            name="email"
                            data-testid="emailInput"
                            value={values.email}
                            onChange={handleChange}
                        />
                    </Form.Item>
                    {!userId && (
                        <Form.Item
                            label={t("fields.password", {ns: "common"})}
                            validateStatus={
                                errors.password && touched.password ? "error" : "success"
                            }
                            help={errors.password && touched.password ? errors.password : null}
                        >
                            <Input.Password
                                name="password"
                                type="password"
                                data-testid="passwordInput"
                                value={values.password}
                                onChange={handleChange}
                            />
                        </Form.Item>
                    )}
                    {stores.meStore.checkRole("selectUserRole") && (
                        <Form.Item
                            label={t("fields.userRole", {ns: "common"})}
                            validateStatus={errors.role && touched.role ? "error" : "success"}
                            help={errors.role && touched.role ? errors.role : null}
                        >
                            <Select
                                showSearch
                                //disabled={userId}
                                name="roleSelect"
                                value={values.role}
                                onChange={value => setFieldValue("role", value)}
                                filterOption={(input, option) =>
                                    option.props.children
                                        .toLowerCase()
                                        .indexOf(input.toLowerCase()) >= 0 ||
                                    option.props.value.toLowerCase().indexOf(input.toLowerCase()) >=
                                        0
                                }
                            >
                                {roleOptions.map(role => (
                                    <Select.Option
                                        key={role.translation_key}
                                        value={role.value}
                                        name="roleSelect_option"
                                    >
                                        {t(role.translation_key, {
                                            ns: "userTypes"
                                        })}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    )}
                    <>
                        <Row type="flex" justify="space-between">
                            <Col span="11">
                                <Form.Item
                                    label={t("fields.phoneNumber", {ns: "common"})}
                                    validateStatus={
                                        errors.phone && touched.phone ? "error" : "success"
                                    }
                                    help={errors.phone && touched.phone ? errors.phone : null}
                                >
                                    <MaskedInput
                                        name="phone"
                                        data-testid="phoneInput"
                                        placeholder="0(555)-555-5555"
                                        mask="1(111)-111-1111"
                                        value={values.phone}
                                        onChange={handleChange}
                                    />
                                </Form.Item>
                            </Col>
                            {["cUser", "sOperation", "sChef"].includes(values.role) && (
                                <Col span="11">
                                    <Form.Item
                                        label={t("fields.title", {ns: "common"})}
                                        validateStatus={
                                            errors.title && touched.title ? "error" : "success"
                                        }
                                        help={errors.title && touched.title ? errors.title : null}
                                    >
                                        <Input
                                            name="title"
                                            data-testid="titleInput"
                                            value={values.title}
                                            onChange={handleChange}
                                        />
                                    </Form.Item>
                                </Col>
                            )}
                        </Row>
                    </>

                    {["hrEmployment", "seller"].includes(values.role) ? (
                        <>
                            <Row type="flex" justify="space-between">
                                <Col span="24">
                                    <CityFilter
                                        showAllOption={false}
                                        value={cities}
                                        mode={"multiple"}
                                        onChange={value => {
                                            setCities(value);
                                        }}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : null}

                    {citySelectableRoles.includes(values.role) ? (
                        <>
                            <Row type="flex" justify="space-between">
                                <Col span="24">
                                    <CityFilter
                                        showAllOption={false}
                                        value={cities}
                                        mode={"multiple"}
                                        onChange={value => {
                                            let errors = [];
                                            const selectedCompanies = companyList.rows.filter(x =>
                                                companies.includes(x._id)
                                            );
                                            if (value.length < cities.length) {
                                                const removedCity = cities.filter(
                                                    x => !value.includes(x)
                                                )[0];
                                                selectedCompanies.forEach((company, index) => {
                                                    const subCompanyList = allSubCompanies[
                                                        index
                                                    ].filter(x =>
                                                        subCompanies[index].includes(x._id)
                                                    );
                                                    const subCompaniesInThatCity = subCompanyList.filter(
                                                        x => x.city == removedCity
                                                    );
                                                    if (subCompaniesInThatCity.length > 0) {
                                                        errors.push({
                                                            type: "subCompany",
                                                            companyName: company.companyName,
                                                            list: subCompaniesInThatCity
                                                        });
                                                    }
                                                });
                                            }

                                            if (errors.length > 0) {
                                                let warningContent = t(
                                                    "validation.wrongSelections",
                                                    {ns: "newUser"}
                                                );
                                                warningContent += `\n\n`;
                                                errors.forEach(error => {
                                                    if (error.type == "subCompany") {
                                                        warningContent += `\n[ ${error.companyName} ]\n`;
                                                        error.list.forEach(item => {
                                                            warningContent += `${item.name}\n`;
                                                        });
                                                        error.list.forEach(item => {
                                                            warningContent += `${item.name}\n`;
                                                        });
                                                        warningContent += "\n";
                                                    }
                                                });
                                                stores.appStore.openWarningModal({
                                                    title: t("modal.warningTitle", {ns: "common"}),
                                                    content: warningContent
                                                });
                                                return false;
                                            } else {
                                                setCities(value);

                                                companies.map((comp, index) => {
                                                    getSubCompany(comp, index, value);
                                                });
                                            }
                                        }}
                                    />
                                </Col>
                            </Row>
                        </>
                    ) : null}

                    {["bm"].includes(values.role) && (
                        <Row type="flex" justify="start">
                            <Col span={24}>
                                <ZoneManagementSelect
                                    value={values.auth.zoneManagement}
                                    onChange={value => setFieldValue("auth.zoneManagement", value)}
                                    showTitle={true}
                                    allowClear={true}
                                />
                            </Col>
                        </Row>
                    )}

                    {["sy", "ik", "agency", "agencyIk", "subAgency"].includes(values.role) && (
                        <Row type="flex" justify="start">
                            <Col span={24}>
                                <ZoneSelect
                                    value={values.auth.zones}
                                    onChange={value => setFieldValue("auth.zones", value)}
                                    mode="multiple"
                                    showTitle={true}
                                    allowClear={true}
                                    placeholder="Sorumlu olduğu bölgeleri seçiniz"
                                />
                            </Col>
                        </Row>
                    )}

                    {["bm", "sy", "ik"].includes(values.role) && (
                        <Row type="flex" justify="start">
                            <Col span={24}>
                                <h5>Denetleme Bölgeleri</h5>
                                <ZoneSelect
                                    value={values.auth.auditZones}
                                    onChange={value => setFieldValue("auth.auditZones", value)}
                                    mode="multiple"
                                    showTitle={false}
                                    allowClear={true}
                                    placeholder="Denetleyeceği bölgeleri seçiniz (bu bölgelerdeki acente kaynaklarına erişim sağlar)"
                                />
                            </Col>
                        </Row>
                    )}

                    {["cUser", "sOperation", ...cityScopedRoles].includes(values.role)
                        ? companies?.map((company, index) => (
                              <Row
                                  type="flex"
                                  key={"row" + company?._id + index}
                                  justify="space-between"
                              >
                                  <Col span="11">
                                      <Form.Item
                                          label={t("fields.company", {ns: "common"})}
                                          extra={
                                              values.role === "subAgency" && index === 0
                                                  ? "Acente Temsilcisi için Firma seçimi yapılmaz ise bölgesindeki tüm firma ve alt firmaları görebilir."
                                                  : undefined
                                          }
                                      >
                                          <Select
                                              showSearch
                                              allowClear
                                              filterOption={(input, option) =>
                                                  option.props.children
                                                      .toLowerCase()
                                                      .indexOf(input.toLowerCase()) >= 0 ||
                                                  option.props.value
                                                      .toLowerCase()
                                                      .indexOf(input.toLowerCase()) >= 0
                                              }
                                              name="companies"
                                              value={
                                                  companyList?.rows?.find(
                                                      rowComp => rowComp?._id === company
                                                  )?.companyName
                                              }
                                              onChange={value => {
                                                  if (value) {
                                                      getSubCompany(value, index, cities);
                                                  } else {
                                                      setAllSubCompanies(prev =>
                                                          prev.map((item, prevIndex) =>
                                                              prevIndex === index
                                                                  ? [{name: "", _id: ""}]
                                                                  : item
                                                          )
                                                      );
                                                  }
                                                  setCompanies(prev =>
                                                      prev.map((item, prevIndex) =>
                                                          prevIndex === index ? value ?? "" : item
                                                      )
                                                  );
                                                  setSubCompanies(prev =>
                                                      prev.map((item, prevIndex) =>
                                                          prevIndex === index ? [] : item
                                                      )
                                                  );
                                              }}
                                          >
                                              {companyList?.rows?.map(company =>
                                                  !companies.includes(company?._id) ? (
                                                      <Select.Option
                                                          value={company?._id}
                                                          name="company_option"
                                                          key={company?._id}
                                                      >
                                                          {company.companyName}
                                                      </Select.Option>
                                                  ) : null
                                              )}
                                          </Select>
                                          {index === companies.length - 1 ? (
                                              <>
                                                  <Button
                                                      onClick={() => {
                                                          setCompanies(prev => [...prev, ""]);
                                                          setSubCompanies(prev => [...prev, []]);
                                                          setAllSubCompanies(prev => [
                                                              ...prev,
                                                              [{name: "", _id: ""}]
                                                          ]);
                                                      }}
                                                      type="link"
                                                  >
                                                      +{" "}
                                                      {t("newUser.actions.addCompany", {
                                                          ns: "newUser"
                                                      })}
                                                  </Button>
                                              </>
                                          ) : null}

                                          {companies.length > 1 && (
                                              <Button
                                                  onClick={() => {
                                                      setCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                      setSubCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                      setAllSubCompanies(prev =>
                                                          prev.filter(
                                                              (item, prevIndex) =>
                                                                  prevIndex !== index
                                                          )
                                                      );
                                                  }}
                                                  type="link"
                                              >
                                                  {t("newUser.actions.removeCompany", {
                                                      ns: "newUser"
                                                  })}
                                              </Button>
                                          )}
                                      </Form.Item>
                                  </Col>
                                  <Col span="11">
                                      {company ? (
                                          <Form.Item
                                              label={t("fields.subcompany", {
                                                  ns: "common"
                                              })}
                                          >
                                              <Select
                                                  showSearch
                                                  name="subcompanies"
                                                  value={subCompanies[index]}
                                                  mode="multiple"
                                                  filterOption={(input, option) =>
                                                      option.props.children
                                                          .toLowerCase()
                                                          .indexOf(input.toLowerCase()) >= 0 ||
                                                      option.props.value
                                                          .toLowerCase()
                                                          .indexOf(input.toLowerCase()) >= 0
                                                  }
                                                  onChange={value => {
                                                      setSubCompanies(prev =>
                                                          prev.map((item, prevIndex) =>
                                                              prevIndex === index ? value : item
                                                          )
                                                      );
                                                  }}
                                              >
                                                  {allSubCompanies[index]?.map(subComp => (
                                                      <Select.Option
                                                          value={subComp?._id}
                                                          name="subcomp_option"
                                                      >
                                                          {subComp.name}
                                                      </Select.Option>
                                                  ))}
                                              </Select>
                                          </Form.Item>
                                      ) : null}
                                  </Col>
                                  {stores.meStore.checkRole("isAdmin") && (
                                      <Col>
                                          <span>Uat Bilgisi</span>
                                          <Switch
                                              checked={uatSwitch}
                                              onChange={checked => setUatSwitch(checked)}
                                              style={{marginLeft: "15px"}}
                                          />
                                      </Col>
                                  )}
                              </Row>
                          ))
                        : null}

                    {!["cUser", "subAgency"].includes(values.role) && (
                        <Col span="11" style={{marginTop: "20px"}}>
                            <span>{t("fields.testUser", {ns: "common"})}</span>
                            <Switch
                                checked={isTest}
                                onChange={() => setIsTest(prev => !prev)}
                                style={{marginLeft: "15px"}}
                            />
                        </Col>
                    )}
                    {["admin", "hrEmployment", "ik"].includes(values.role) && (
                        <Col span="11" style={{marginTop: "20px"}}>
                            <span>Kuryeleri Arayabilir</span>
                            <Switch
                                checked={canCallCouriers}
                                onChange={checked => {
                                    if (checked) {
                                        setQueueIdModalVisible(true);
                                    } else {
                                        setCanCallCouriers(false);
                                        setQueueId(null);
                                    }
                                }}
                                style={{marginLeft: "15px"}}
                            />
                        </Col>
                    )}
                    {canCallCouriers && queueId && (
                        <Col span="11" style={{marginTop: "20px"}}>
                            <span>Kuyruk ID: {queueId}</span>
                        </Col>
                    )}

                    {values.role === "subAgency" && (
                        <PagePermissions
                            value={values.auth.pagePermissions}
                            onChange={keys => setFieldValue("auth.pagePermissions", keys)}
                        />
                    )}

                    <CommunicationPreferencesCardContainer
                        title={
                            <h4>
                                <NotificationOutlined />
                                <HeaderText>
                                    {t("communicationPreferences", {
                                        ns: "communicationPreferences"
                                    })}
                                </HeaderText>
                            </h4>
                        }
                    >
                        <List.Item>
                            <Skeleton loading={false} active>
                                <div>
                                    {t("communicationPermission", {ns: "communicationPreferences"})}
                                </div>
                            </Skeleton>
                            <Switch checked={dailyReminder} onChange={onChangeSwitch} />
                        </List.Item>
                    </CommunicationPreferencesCardContainer>

                    <Row type="flex" justify="end">
                        <CustomButton
                            data-testid="submitButton"
                            type="primary"
                            htmlType="submit"
                            disabled={isSubmitting}
                        >
                            {t("actions.save", {ns: "common"})}
                        </CustomButton>
                    </Row>
                </Form>
            </Col>
            <AuthorizedCityUsersModal
                visible={authorizedUsersModalVisible}
                onClose={() => setAuthorizedUsersModalVisible(false)}
                title={authorizedUsersData.title}
                authorizedCityUsers={authorizedUsersData.users}
            />
            <QueueIdModal
                visible={queueIdModalVisible}
                onCancel={() => setQueueIdModalVisible(false)}
                onConfirm={selectedQueueId => {
                    setQueueId(selectedQueueId);
                    setCanCallCouriers(true);
                    setQueueIdModalVisible(false);
                }}
                initialValue={queueId}
            />
        </Row>
    );
};

export const SelectField = styled(Select)`
    width: 100%;
    @media (max-width: 768px) {
        display: flex;
        width: 100%;
        .ant-select-selector {
            min-width: 197px;
            font-size: 12px;
        }
    }
`;
export const Wrapper = styled.div`
    margin-top: 20px;
    padding: 20px;
    border: 1px solid #e8e8e8;
    border-radius: 5px;
    background-color: #f0f0f0;
`;
export const WrapperCompanies = styled.div`
    margin-top: 20px;
`;
